"use client";

import React from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { alpha } from "@mui/material";
import Image from "next/image";
import InfoIcon from "@mui/icons-material/Info";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { motion } from "framer-motion";
import { FormatedTime } from "../../genericComponents/formated-time/FormatedTime";

export interface CartLineItemProps {
  item: any;
  currencyCode: string;
  isLineLoading: boolean;
  onDecrease: () => void;
  onIncrease: () => void;
  onRemove: () => void;
  onInfo: () => void;
}

export const CartLineItem = React.memo(function CartLineItem({
  item,
  currencyCode,
  isLineLoading,
  onDecrease,
  onIncrease,
  onRemove,
  onInfo,
}: CartLineItemProps) {
  const linePrice = item?.specialPrice ? item?.specialPrice : item?.totalAmount;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
      transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
      style={{ width: "100%", overflow: "hidden" }}
    >
      <Box
        sx={{
          borderRadius: "18px",
          width: "100%",
          background: "#fff",
          border: `1px solid ${alpha("#1f7a4d", 0.12)}`,
          borderLeft: `4px solid #1f7a4d`,
          boxShadow:
            "0 10px 28px rgba(15, 23, 17, 0.06), 0 0 0 1px rgba(255,255,255,0.85) inset",
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 72,
                height: 72,
                minWidth: 72,
                borderRadius: "14px",
                overflow: "hidden",
                bgcolor: alpha("#1f7a4d", 0.06),
                flexShrink: 0,
              }}
            >
              {item?.ImageUrl ? (
                <Image
                  src={item.ImageUrl}
                  alt={item?.serviceName ?? "Service"}
                  fill
                  sizes="72px"
                  style={{ objectFit: "cover" }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: alpha("#1f7a4d", 0.35),
                    fontSize: "0.7rem",
                    fontWeight: 700,
                  }}
                >
                  Svc
                </Box>
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.25,
                      fontSize: "0.95rem",
                      letterSpacing: "-0.02em",
                      color: "#0b1710",
                    }}
                  >
                    {item?.serviceName}
                  </Typography>
                  {item?.subHeading ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.35, lineHeight: 1.35 }}
                    >
                      {item?.subHeading}
                    </Typography>
                  ) : null}
                </Box>
                <InfoIcon
                  sx={{
                    color: "#1f7a4d",
                    cursor: "pointer",
                    fontSize: 22,
                    flexShrink: 0,
                    mt: 0.15,
                  }}
                  onClick={onInfo}
                  aria-label="Service details"
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  gap: 0.75,
                  mt: 1,
                  rowGap: 0.25,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 800,
                    fontSize: "1rem",
                    fontVariantNumeric: "tabular-nums",
                    color: "#0b1710",
                  }}
                >
                  {currencyCode}
                  {linePrice}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    textDecoration: "line-through",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {currencyCode}
                  {item?.totalMrp}
                </Typography>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  <Box component="span" sx={{ opacity: 0.45, mx: 0.5 }}>
                    ·
                  </Box>
                  <FormatedTime min={item?.min} />
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mt: 1.25,
                }}
              >
                {isLineLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 120,
                      height: 40,
                      borderRadius: "12px",
                      border: `1px solid ${alpha("#0b1710", 0.12)}`,
                      bgcolor: alpha("#f5f5f5", 0.6),
                    }}
                  >
                    <CircularProgress size={22} color="success" />
                  </Box>
                ) : (
                  <motion.div
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    style={{ display: "inline-block" }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minWidth: 120,
                        height: 40,
                        px: 0.5,
                        borderRadius: "12px",
                        border: `1px solid ${alpha("#1f7a4d", 0.35)}`,
                        bgcolor: alpha("#1f7a4d", 0.04),
                      }}
                    >
                    <RemoveIcon
                      onClick={onDecrease}
                      sx={{
                        cursor: "pointer",
                        fontSize: 22,
                        p: 0.5,
                        borderRadius: "8px",
                        color: "#1f7a4d",
                        "&:hover": { bgcolor: alpha("#1f7a4d", 0.1) },
                      }}
                      aria-label="Decrease quantity"
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 800, minWidth: 24, textAlign: "center" }}
                    >
                      {item.count}
                    </Typography>
                    <AddIcon
                      onClick={onIncrease}
                      sx={{
                        cursor: "pointer",
                        fontSize: 22,
                        p: 0.5,
                        borderRadius: "8px",
                        color: "#1f7a4d",
                        "&:hover": { bgcolor: alpha("#1f7a4d", 0.1) },
                      }}
                      aria-label="Increase quantity"
                    />
                    </Box>
                  </motion.div>
                )}
                <Button
                  variant="text"
                  size="small"
                  onClick={onRemove}
                  disabled={isLineLoading}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    color: "error.main",
                    minWidth: "auto",
                    px: 1,
                  }}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          </Box>

          {item?.specialMessage ? (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 1.25,
                color: "#1f7a4d",
                fontWeight: 600,
              }}
            >
              {item?.specialMessage}
            </Typography>
          ) : null}

          <Box
            sx={{
              background: alpha("#1f7a4d", 0.06),
              border: `1px solid ${alpha("#1f7a4d", 0.15)}`,
              mt: 1.25,
              p: 1.25,
              borderRadius: "12px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Service charges
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
              >
                {currencyCode}
                {item?.serviceCharge}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 0.75,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Product cost
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
              >
                {currencyCode}
                {item?.productCost}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
});
